import React, { useState, useEffect } from "react";
import FaqSection from "../components/FaqSection";
import { Helmet } from "react-helmet-async";
import zohoStyles from "./ZohoFinal.module.css";
import Navigation from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Plus,
  Minus,
  Phone,
  Mail,
  ArrowRight,
  Building2,
  Shield,
  Users2,
  BookOpen,
  IdCard,
  Package,
  PieChart,
  Network,
  PlugZap,
  // CheckCircle2,
} from "lucide-react";

/* ✅ Import images from src/assets */
import ZohoLogo from "../assests/images/zoho-partner-logo-2.png";
import FutopsLogo from "../assests/images/Futops.png";
import MensaLogo from "../assests/images/mensa.png";
import HungerboxLogo from "../assests/images/hungerbox.svg";

const siteUrl = "https://www.cloudare.in/services-zoho";
const phoneDisplay = "+1 628-282-5683";
const phoneHref = "+16282825683";
const email = "sales@cloudare.in";
const calendly = "https://calendly.com/cloudare";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Cloudare Technologies",
  url: "https://www.cloudare.in",
  logo: "https://www.cloudare.in/logo192.png",
  sameAs: [],
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Zoho Implementation & Support",
  description:
    "Empowering businesses in Tier-2 cities like Raipur (CG), Indore (MP), Bhubaneswar (Odisha), Nagpur (MH), and Jaipur (RJ) with seamless Zoho CRM, Books, People, Inventory, Integrations, and GST e-invoicing solutions.",
  provider: {
    "@type": "Organization",
    name: "Cloudare Technologies",
    url: "https://www.cloudare.in",
  },
  areaServed: [
    "Raipur (Chhattisgarh, India)",
    "Indore (Madhya Pradesh, India)",
    "Bhubaneswar (Odisha, India)",
    "Nagpur (Maharashtra, India)",
    "Jaipur (Rajasthan, India)",
    "Central India (India)",
    "Kingdom of Saudi Arabia (KSA)",
    "Malaysia",
  ],
};

const faqList = [
  {
    q: "Which Zoho products can your team implement?",
    a: "We implement a complete suite of Zoho solutions including CRM, Books, People, Inventory, Desk, Analytics, and more — tailored to your business needs.",
  },
  {
    q: "Do you handle GST and e-invoicing in Zoho Books?",
    a: "Absolutely — we configure GST, generate e-invoices, manage IRN/QR codes, and ensure seamless reconciliation directly from Zoho Books.",
  },
  {
    q: "Can you migrate our data from legacy systems to Zoho?",
    a: "Yes — our team performs data extraction, cleansing, mapping, and safe imports, ensuring smooth migration with full reconciliation.",
  },
  {
    q: "Do you offer Zoho customization and automation?",
    a: "Yes — we create custom functions, Deluge scripts, workflows, blueprints, and UI enhancements to automate processes and boost productivity.",
  },
  {
    q: "Can Zoho integrate with other ERPs or Salesforce?",
    a: "Absolutely — we enable reliable REST/SOAP/Webhook integrations and middleware solutions to sync data across all your business systems.",
  },
  {
    q: "What support options are available?",
    a: "We provide flexible support plans including hourly assistance, monthly retainer packages, and SLA-based managed services with priority ticketing.",
  },
  {
    q: "How much does a Zoho implementation typically cost?",
    a: "Costs depend on scope — after a discovery session, we provide transparent, fixed-scope quotes with affordable options for SMEs.",
  },
  {
    q: "Do you implement invoice discounting workflows in Zoho Books?",
    a: "Yes — we have successfully implemented invoice discounting flows for clients like Hungerbox and Eatgood, fully integrated with Zoho Books.",
  },
  {
    q: "Do you train users and admins on Zoho?",
    a: "Yes — we provide role-based training sessions, admin handover, user manuals, and recorded sessions to ensure smooth adoption.",
  },
  {
    q: "How long does a typical Zoho implementation take?",
    a: "Small implementations take 3–6 weeks, while medium-sized projects range from 6–12 weeks depending on integrations and customizations.",
  },
  {
    q: "Can you set up multi-company and multi-warehouse configurations?",
    a: "Yes — we configure Zoho Inventory and Books for multi-company and multi-warehouse operations, fully aligned with your accounting needs.",
  },
  {
    q: "Is Zoho suitable for manufacturing, retail, and service businesses?",
    a: "Absolutely — we tailor Zoho solutions for manufacturing, retail, and service industries, optimizing operations and reporting efficiency.",
  },
  {
    q: "Do you provide Zoho services for businesses in Raipur (CG)?",
    a: "Yes — we support SMEs and enterprises in Raipur (Chhattisgarh) with complete Zoho implementation, GST compliance, and managed services customized for local business requirements.",
  },
  {
    q: "Do you provide Zoho services for businesses in Tier-2 cities?",
    a: "Yes — we support SMEs and enterprises in Tier-2 cities like Raipur (Chhattisgarh), Indore (Madhya Pradesh), Bhubaneswar (Odisha), Nagpur (Maharashtra), Jaipur (Rajasthan), as well as countries including India, KSA, Malaysia, US, and Canada. Our services include complete Zoho implementation, GST compliance, and managed support tailored to local business needs.",
  },
  {
    q: "Do you provide Zoho services for businesses outside India?",
    a: "Yes — we serve businesses across multiple countries including KSA, Malaysia, the US, Canada, and other global markets. Our services cover complete Zoho implementation, GST/e-invoicing integrations (where applicable), and managed support tailored to each region’s compliance requirements.",
  },
];

export default function ZohoServicesPage() {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  return (
    <main className={zohoStyles.main}>
      <Navigation />
      <Helmet>
        <title>
          Zoho Partner in Raipur (CG) — Zoho Implementation & Support | Cloudare
        </title>
        <meta
          name="description"
          content="Cloudare — Zoho Partner in Raipur, Chhattisgarh (CG). Zoho CRM, Zoho Books, Zoho People, GST e-invoicing and integrations across Odisha & Central India."
        />
        <meta
          name="keywords"
          content="Zoho Raipur, Zoho CG, Zoho Chhattisgarh, Zoho Odisha, Zoho CRM Raipur, Zoho Books Odisha, Zoho Support CG, Zoho Partner Raipur"
        />
        <link rel="canonical" href={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqList.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className={zohoStyles.hero}>
        <div className={zohoStyles.containerGrid}>
          <div className={zohoStyles.headContainer}>
            <img
              src={ZohoLogo}
              alt="Zoho partner logo"
              className={zohoStyles.heroLogo}
            />
            <h1 className={zohoStyles.heroTitle1}>
              Zoho Implementation & Support India & Beyond
            </h1>
            <p className={zohoStyles.heroDesc}>
              We offer comprehensive Zoho solutions—including CRM, Books,
              People, Inventory, and GST e-invoicing integrations—ensuring full
              local compliance, dedicated support, and seamless service for
              businesses across India, KSA, Malaysia, the US, Canada, and other
              global markets.
            </p>

            <div className={zohoStyles.heroButtons}>
              <a
                href={calendly}
                target="_blank"
                rel="noopener noreferrer"
                className={zohoStyles.btnPrimary}
              >
                Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
              </a>
              <a href="#faq" className={zohoStyles.btnSecondary}>
                View FAQs <ArrowRight className={zohoStyles.iconSm} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>Zoho Services We Offer</h2>
          <div className={zohoStyles.appGrid}>
            <AppCard
              title="Zoho CRM"
              desc="Sales automation, pipelines, blueprints, leads, contacts, and custom modules."
              icon={<Users2 className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Zoho Books (Cloud Books)"
              desc="GST accounting, invoicing, reconcile, e-invoicing and financial reports."
              icon={<BookOpen className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Zoho People & Payroll"
              desc="Attendance, leave, payroll integration and statutory reports."
              icon={<IdCard className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Zoho Inventory"
              desc="Multi-warehouse, serial/batch, barcodes, order management."
              icon={<Package className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Zoho Analytics"
              desc="Dashboards, KPIs, scheduled reports and data blending."
              icon={<PieChart className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Integrations & APIs"
              desc="Salesforce, payment gateways, courier, marketplace and custom middleware."
              icon={<Network className={zohoStyles.iconLg} />}
            />
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            Why Choose Zoho with Cloudare
          </h2>
          <div className={zohoStyles.featureGrid}>
            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <Building2 className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>
                  Affordable & Modular
                </h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                Choose only the apps you need — scale up easily without heavy
                upfront costs.
              </p>
            </div>

            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <Shield className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>Local Compliance</h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                GST e-invoicing, IRN/QR generation, and regional tax support for
                India and neighboring countries.
              </p>
            </div>

            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <PlugZap className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>
                  Integrations & Automations
                </h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                Connect Zoho with payment gateways, logistics, marketplaces and
                other ERPs for seamless operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>Success Stories</h2>
          <p className={zohoStyles.sectionDesc}>
            Selected Zoho deployments and outcomes we delivered.
          </p>
          <div className={zohoStyles.caseGrid}>
            <CaseStudy
              logo={FutopsLogo}
              alt="Futops logo"
              title="Futops — Zoho Books & CRM Automation"
              bullets={[
                "Automated invoicing & reconciliations",
                "Sales CRM automation & dashboards",
              ]}
            />
            <CaseStudy
              logo={MensaLogo}
              alt="Mensa logo"
              title="Mensa — E-Invoicing & GST Automation in Books"
              bullets={["IRN generation & filing", "Auto GST reconciliations"]}
            />
            <CaseStudy
              logo={HungerboxLogo}
              alt="Hungerbox / Eatgood logo"
              title="Hungerbox / Eatgood — Invoice Discounting in Zoho Books"
              bullets={[
                "Invoice discounting workflow",
                "Faster receivables & cashflow",
              ]}
            />
          </div>
          <div className={zohoStyles.statGrid}>
            <StatCard
              label="Successful Automations"
              value="40+"
              maxProgress={60}
            />
            <StatCard
              label="Average IRN Accuracy"
              value="100%"
              maxProgress={90}
            />
            <StatCard
              label="Average Go-Live Time"
              value="3-4 weeks"
              maxProgress={50}
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>What Our Clients Say</h2>
          <div className={zohoStyles.grid3}>
            <Testimonial
              name="R. Sharma, Futops"
              text="Cloudare automated our invoicing & CRM workflows. Reduced reconciliation time by 80%."
            />
            <Testimonial
              name="M. Tan, Mensa"
              text="E-invoicing and GST automation was seamless — excellent execution and support."
            />
            <Testimonial
              name="S. Kapoor, Eatgood"
              text="Invoice discounting flow improved our cash cycle significantly."
            />
          </div>
        </div>
      </section>

      <section id="contact" className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <div className={zohoStyles.contactCard}>
            <div className={zohoStyles.contact1}>
              <h2 className={zohoStyles.sectionTitle}>
                Connect with our Expert
              </h2>
              <p className={zohoStyles.heroDesc}>Book a free 30-min Call.</p>
              <div className={zohoStyles.heroButtons}>
                {/* <a href={calendly} className={zohoStyles.btnPrimary}>
                  Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
                </a> */}
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={zohoStyles.btnPrimary}
                >
                  Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
                </a>

                <a href={`mailto:${email}`} className={zohoStyles.btnSecondary}>
                  <Mail className={zohoStyles.iconSm} /> {email}
                </a>
              </div>
            </div>
            <div className={zohoStyles.contactInfo}>
              <ContactRow
                label="Phone"
                value={phoneDisplay}
                href={`tel:${phoneHref}`}
                icon={<Phone className={zohoStyles.iconSm} />}
              />
              {/* <ContactRow
                label="Email"
                value={email}
                href={`mailto:${email}`}
                icon={<Mail className={zohoStyles.iconSm} />}
              /> */}
              {/* <div className={zohoStyles.locationText}>
                📍 India, KSA, Malaysia, US, Canada
              </div> */}
            </div>
          </div>
        </div>
      </section>
      <div id="faq">
        <FaqSection
          title="Zoho ERP & Books — FAQ"
          faqs={faqList.map((x) => ({ question: x.q, answer: x.a }))}
          emitJsonLd
        />
      </div>

      {/* Contact CTA */}

      <Footer />
    </main>
  );
}

/* ✅ Subcomponents */
/* ✅ Service Card */
function AppCard({ title, desc, icon }) {
  return (
    <div className={zohoStyles.appCard}>
      <div className={zohoStyles.appHeader}>
        <div className={zohoStyles.appIcon}>{icon}</div>
        <h3 className={zohoStyles.appTitle}>{title}</h3>
      </div>
      <p className={zohoStyles.appDesc}>{desc}</p>
    </div>
  );
}

/* ✅ Case Study */
function CaseStudy({ logo, alt, title, bullets }) {
  return (
    <div className={zohoStyles.caseCard}>
      <div className={zohoStyles.caseImage}>
        <img src={logo} alt={alt} className={zohoStyles.caseLogo} />
      </div>
      <div>
        {/* <img src={logo} alt={alt} className={zohoStyles.caseLogo} /> */}
        <h3 className={zohoStyles.caseTitle}>{title}</h3>
        <ul className={zohoStyles.caseList}>
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value, maxProgress = 70 }) {
  // maxProgress in %
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200; // total animation duration
    const stepTime = 20;
    const step = Math.ceil(maxProgress / (duration / stepTime));

    const interval = setInterval(() => {
      start += step;
      if (start >= maxProgress) {
        start = maxProgress;
        clearInterval(interval);
      }
      setProgress(start);
    }, stepTime);

    return () => clearInterval(interval);
  }, [maxProgress]);

  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className={zohoStyles.statCard}>
      <svg
        className={zohoStyles.circleChart}
        width="100"
        height="100"
        viewBox="0 0 100 100"
      >
        {/* Background */}
        <circle
          className={zohoStyles.circleBg}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
        />
        {/* Animated Progress */}
        <circle
          className={zohoStyles.circleProgress}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
        {/* Display value as entered */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="middle"
          className="statValue"
        >
          {value}
        </text>
      </svg>
      <div className="statLabel">{label}</div>
    </div>
  );
}
/* ✅ Testimonial */
function Testimonial({ name, text }) {
  return (
    <div className={zohoStyles.proCard}>
      <div className={zohoStyles.testimonialBox}>
        <div className={zohoStyles.avatar}></div>
        <div>
          <div className={zohoStyles.testimonialName}>{name}</div>
          <div className={zohoStyles.testimonialText}>{text}</div>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ label, value, href, icon }) {
  return (
    <div className={zohoStyles.contactRow}>
      <div className={zohoStyles.contactIcon}>{icon}</div>
      <div>
        <div className={zohoStyles.contactLabel}>{label}</div>
        <a href={href} className={zohoStyles.contactValue}>
          {value}
        </a>
      </div>
    </div>
  );
}
